import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class M3PayorAllocateService {
  private baseUrl: string = '';

  constructor(private http: HttpClient) {}

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  private toUrlEncoded(params: any): string {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });
    return urlEncoded.toString();
  }

  private getUrlEncodedHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
  }

  /**
   * Action code: q7a4cr
   */
  getPayorAccountDetails(leKeys: string, paymentType: string, classificationType: string, transType: string, ssoId: string, globalParams: any): Observable<any> {
    const gp = globalParams || {};
    const params: any = {
      action_code: 'q7a4cr',
      sso_id: ssoId,
      tax_year: gp.tax_year,
      scenario: gp.scenario,
      jcd_key: gp.jcd_key,
      le_keys: leKeys,
      payment_type: paymentType,
      classification_type: classificationType,
      trans_type: transType
    };

    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, {
      params,
      withCredentials: true
    }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1') {
          return response;
        } else {
          throw new Error(response.errorMessage || 'Failed to load payor account details');
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * Action code: zp7sok
   */
  getThirdPartyPayorList(globalParams: any): Observable<any> {
    const gp = globalParams || {};
    const params: any = {
      action_code: 'zp7sok',
      tax_year: gp.tax_year,
      scenario: gp.scenario,
      jcd_key: gp.jcd_key
    };

    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, {
      params,
      withCredentials: true
    }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1') {
          return response;
        } else {
          throw new Error(response.errorMessage || 'Failed to load third party payor list');
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * Action code: eosx7n
   */
  getTradingPartners(lineNo: string, searchString: string, globalParams: any): Observable<any> {
    const gp = globalParams || {};
    const params: any = {
      action_code: 'eosx7n',
      tax_year: gp.tax_year,
      scenario: gp.scenario,
      jcd_key: gp.jcd_key,
      search_string: searchString,
      line_no: lineNo
    };

    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, {
      params,
      withCredentials: true
    }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response.jsonObject;
        } else {
          throw new Error(response.errorMessage || 'Failed to load trading partners');
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * Action code: y0onfe
   */
  saveRPModal(finalData: any[], clobSettings: any[], classificationType: string, transType: string, globalParams: any, filterParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      ...(filterParams || {}),
      json_data: JSON.stringify(finalData),
      json_settings: JSON.stringify(clobSettings),
      classification_type: classificationType,
      trans_type: transType
    };

    return this.http.post<any>(
      `${this.baseUrl}/saveJsonObject?action_code=y0onfe`,
      this.toUrlEncoded(params),
      { headers: this.getUrlEncodedHeaders(), withCredentials: true }
    );
  }

  /**
   * Action code: oexyp0
   */
  allocateProcess(allocateData: any[], clobSettings: any[], globalParams: any, filterParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      ...(filterParams || {}),
      json_data: JSON.stringify(allocateData),
      json_settings: JSON.stringify(clobSettings)
    };

    return this.http.post<any>(
      `${this.baseUrl}/saveJsonObject?action_code=oexyp0`,
      this.toUrlEncoded(params),
      { headers: this.getUrlEncodedHeaders(), withCredentials: true }
    );
  }

  /**
   * Action code: o3m4sg
   */
  saveThirdPartyPayor(payorName: string, payorEin: string, globalParams: any, filterParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      ...(filterParams || {}),
      payor_name: payorName,
      payor_ein: payorEin
    };

    return this.http.post<any>(
      `${this.baseUrl}/saveJsonObject?action_code=o3m4sg`,
      this.toUrlEncoded(params),
      { headers: this.getUrlEncodedHeaders(), withCredentials: true }
    );
  }
}
